import { useState, useRef, useEffect } from "react";
import { Globe } from "lucide-react";
import { useLocale } from "@/contexts/LocaleContext";
import { Locale } from "@/i18n";

const LOCALES: { code: Locale; label: string; flag: string }[] = [
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "lv", label: "Latviešu", flag: "🇱🇻" },
  { code: "ru", label: "Русский", flag: "🇷🇺" },
];

interface LanguageSelectorProps {
  /** compact = icon only, default = flag + code */
  variant?: "compact" | "full" | "menu-item";
}

export function LanguageSelector({ variant = "full" }: LanguageSelectorProps) {
  const { locale, setLocale } = useLocale();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const current = LOCALES.find((l) => l.code === locale) ?? LOCALES[0];

  if (variant === "menu-item") {
    return (
      <div className="relative" ref={ref}>
        <button
          onClick={() => setOpen(!open)}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-muted/30 transition-colors group"
        >
          <div className="w-7 h-7 rounded-lg bg-muted/30 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
            <Globe className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          <div className="flex-1 text-left">
            <div className="text-xs font-medium text-foreground">Language</div>
            <div className="text-xs text-muted-foreground">{current.flag} {current.label}</div>
          </div>
        </button>

        {open && (
          <div className="absolute left-0 bottom-full mb-1 w-48 rounded-xl border border-border/50 bg-popover/95 backdrop-blur-xl shadow-cyber z-50 overflow-hidden">
            {LOCALES.map((l) => (
              <button
                key={l.code}
                onClick={() => { setLocale(l.code); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-xs hover:bg-muted/30 transition-colors ${locale === l.code ? "text-primary bg-primary/5" : "text-foreground"}`}
              >
                <span className="text-base">{l.flag}</span>
                <span className="font-medium">{l.label}</span>
                {locale === l.code && <span className="ml-auto text-primary">✓</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 rounded-lg border border-border/50 bg-muted/20 hover:bg-muted/30 hover:border-primary/30 transition-all text-xs font-medium ${
          variant === "compact" ? "p-2" : "px-3 py-1.5"
        }`}
        aria-label={variant === "compact" ? "Language selector" : `${current.label} – Language selector`}
      >
        {variant === "compact" ? (
          <Globe className="w-4 h-4 text-muted-foreground" />
        ) : (
          <>
            <span className="text-base leading-none">{current.flag}</span>
            <span className="text-foreground hidden sm:inline">{current.label}</span>
          </>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-44 rounded-xl border border-border/50 bg-popover/95 backdrop-blur-xl shadow-cyber z-50 overflow-hidden">
          {LOCALES.map((l) => (
            <button
              key={l.code}
              onClick={() => { setLocale(l.code); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-xs hover:bg-muted/30 transition-colors ${locale === l.code ? "text-primary bg-primary/5" : "text-foreground"}`}
            >
              <span className="text-base">{l.flag}</span>
              <span className="font-medium">{l.label}</span>
              {locale === l.code && <span className="ml-auto text-primary">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
